import { Loader2, Check, X, AlertTriangle, Minus } from 'lucide-react'

// ── Types ─────────────────────────────────────────────────────────────────────

interface WorkflowSteps {
  order?: string[]
  passed?: string[]
  failed?: string[]
  incomplete?: string[]
  notApplicable?: string[]
}

interface ProcessResult {
  objectType?: string
  result?: string
  providerResult?: { source?: string }
}

interface WorkflowStepResult {
  stepName?: string
  result?: string
  processResults?: ProcessResult[]
}

interface RiskFactor {
  name?: string
  score?: number
}

interface RiskAssessment {
  riskLevel?: string
  riskScore?: number
  riskFactors?: RiskFactor[]
}

// ── Step Color Logic ──────────────────────────────────────────────────────────

type StepState = 'pass' | 'fail' | 'risk' | 'incomplete' | 'running' | 'pending' | 'selected'

interface StepColors {
  bg: string
  border: string
  text: string
}

function getStepState(stepName: string, steps: WorkflowSteps, isRunning: boolean): StepState {
  const name = stepName.toUpperCase()
  if (steps.passed?.includes(name)) return 'pass'
  if (steps.failed?.includes(name)) return 'fail'
  if (steps.incomplete?.includes(name) || steps.notApplicable?.includes(name)) return 'incomplete'
  if (isRunning) return 'running'
  return 'pending'
}

function getStepColors(state: StepState): StepColors {
  switch (state) {
    case 'pass': return { bg: 'rgba(52,211,153,.1)', border: 'rgba(52,211,153,.4)', text: '#6ee7b7' }
    case 'fail': return { bg: 'rgba(248,113,113,.1)', border: 'rgba(248,113,113,.45)', text: '#fca5a5' }
    case 'risk': return { bg: 'rgba(251,146,60,.1)', border: 'rgba(251,146,60,.45)', text: '#fdba74' }
    case 'incomplete': return { bg: '#101a2c', border: '#243149', text: '#475569' }
    case 'running': return { bg: 'rgba(251,191,36,.12)', border: 'rgba(251,191,36,.5)', text: '#fcd34d' }
    case 'pending': return { bg: '#0c1424', border: '#1c2940', text: '#3f4f6b' }
    default: return { bg: '#0c1424', border: '#1c2940', text: '#3f4f6b' }
  }
}

function StepIcon({ state }: { state: StepState }) {
  switch (state) {
    case 'pass': return <Check size={10} strokeWidth={2.5} />
    case 'fail': return <X size={10} strokeWidth={2.5} />
    case 'risk': return <AlertTriangle size={10} strokeWidth={2.5} />
    case 'running': return <Loader2 size={10} className="animate-spin" />
    default: return <Minus size={10} strokeWidth={2} />
  }
}

function StepLabel(name: string): string {
  if (name === 'TOGGLE_MONITORING') return 'MONITORING'
  return name
}

// ── Pipeline Step Node ────────────────────────────────────────────────────────

interface PipelineNodeProps {
  name: string
  steps: WorkflowSteps
  isRunning: boolean
  selected: boolean
  onClick: () => void
}

function PipelineNode({ name, steps, isRunning, selected, onClick }: PipelineNodeProps) {
  const state = getStepState(name, steps, isRunning)
  const colors = getStepColors(state)

  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-1 px-3 py-2 rounded-[9px] transition-all text-left"
      style={{
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        color: colors.text,
        boxShadow: selected ? `0 0 0 2px ${colors.border}` : 'none',
        minWidth: 72,
      }}
    >
      <div className="flex items-center gap-1">
        <StepIcon state={state} />
      </div>
      <span className="text-[9px] font-semibold uppercase tracking-wider">
        {StepLabel(name)}
      </span>
    </button>
  )
}

// ── Workflow Pipeline ─────────────────────────────────────────────────────────

interface WorkflowPipelineProps {
  steps: WorkflowSteps
  isRunning: boolean
  selectedStep: string | null
  onStepClick: (step: string) => void
}

const DEFAULT_STEP_ORDER = ['START', 'MATCHLIST', 'KYC', 'AML', 'TOGGLE_MONITORING', 'DECISION', 'FINISH']

export function WorkflowPipeline({ steps, isRunning, selectedStep, onStepClick }: WorkflowPipelineProps) {
  const order = steps.order?.length ? steps.order : DEFAULT_STEP_ORDER

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {order.map((stepName, i) => (
        <div key={stepName} className="flex items-center gap-2">
          <PipelineNode
            name={stepName}
            steps={steps}
            isRunning={isRunning}
            selected={selectedStep === stepName}
            onClick={() => onStepClick(stepName)}
          />
          {i < order.length - 1 && (
            <span className="text-[#283a57] text-sm font-bold">→</span>
          )}
        </div>
      ))}
    </div>
  )
}

// ── Workflow Flowchart ────────────────────────────────────────────────────────

interface WorkflowFlowchartProps {
  steps: WorkflowSteps
  isRunning: boolean
  selectedStep: string | null
  onStepClick: (step: string) => void
  entityId?: string | null
  workflowName?: string
  workflowExecutionId?: string | null
  overallStatus?: string
  webhookReceived?: boolean
}

function FlowchartConnector() {
  return (
    <div className="flex justify-center">
      <div className="w-0.5 h-6" style={{ background: '#283a57' }} />
    </div>
  )
}

export function WorkflowFlowchart({
  steps,
  isRunning,
  selectedStep,
  onStepClick,
  entityId,
  workflowName,
  workflowExecutionId,
  overallStatus,
  webhookReceived,
}: WorkflowFlowchartProps) {
  const boxStyle = {
    background: '#101d33',
    border: '1px solid #283a57',
    borderRadius: 11,
    padding: '12px 16px',
  }

  const outcomeColor = (() => {
    const s = (overallStatus ?? '').toUpperCase()
    if (s === 'PASS' || s === 'CLEAR') return '#34d399'
    if (s === 'FAIL') return '#f87171'
    if (s === 'REFER' || s === 'REVIEW') return '#fb923c'
    return '#94a3b8'
  })()

  return (
    <div className="space-y-0">
      {/* Top row: API Call → Workflow Execution */}
      <div className="flex items-stretch gap-4">
        <div className="flex-1" style={boxStyle}>
          <p className="text-[9px] font-bold uppercase tracking-wider text-[#60a5fa] mb-2">API Call</p>
          <p className="text-[11px] font-mono text-[#e2e8f0]">POST /individuals</p>
          {entityId && (
            <p className="text-[10px] font-mono text-[#5b6b85] mt-1 truncate">
              entity: {entityId}
            </p>
          )}
        </div>
        <div className="flex items-center">
          <span className="text-[#283a57] font-bold">→</span>
        </div>
        <div className="flex-1" style={boxStyle}>
          <p className="text-[9px] font-bold uppercase tracking-wider text-[#a78bfa] mb-2">Workflow Execution</p>
          <p className="text-[11px] font-mono text-[#e2e8f0]">{workflowName || '—'}</p>
          {workflowExecutionId && (
            <p className="text-[10px] font-mono text-[#5b6b85] mt-1 truncate">
              exec: {workflowExecutionId.slice(0, 20)}…
            </p>
          )}
        </div>
      </div>

      <FlowchartConnector />

      {/* Workflow steps */}
      <div style={boxStyle}>
        <p className="text-[9px] font-bold uppercase tracking-wider text-[#a78bfa] mb-3">Workflow Steps Pipeline</p>
        <WorkflowPipeline
          steps={steps}
          isRunning={isRunning}
          selectedStep={selectedStep}
          onStepClick={onStepClick}
        />
      </div>

      <FlowchartConnector />

      {/* Webhook received */}
      <div
        style={{
          ...boxStyle,
          background: webhookReceived ? `rgba(${overallStatus === 'FAIL' ? '248,113,113' : '52,211,153'},.08)` : '#101d33',
          borderColor: webhookReceived ? outcomeColor + '40' : '#283a57',
        }}
      >
        <div className="flex items-center gap-2">
          <span
            className="relative flex h-2 w-2"
          >
            {!webhookReceived && (
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            )}
            <span
              className="relative inline-flex rounded-full h-2 w-2"
              style={{ background: webhookReceived ? outcomeColor : '#34d399' }}
            />
          </span>
          <p
            className="text-[9px] font-bold uppercase tracking-wider"
            style={{ color: webhookReceived ? outcomeColor : '#475569' }}
          >
            Webhook {webhookReceived ? 'Received' : 'Waiting…'}
          </p>
        </div>
        {webhookReceived && overallStatus && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span
              className="text-[10px] font-bold px-1.5 py-0.5 rounded"
              style={{ background: `${outcomeColor}20`, color: outcomeColor }}
            >
              {overallStatus}
            </span>
            {workflowName && (
              <span className="text-[9px] font-mono text-[#475569]">{workflowName}</span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Step Detail Drawer ────────────────────────────────────────────────────────

interface StepDetailDrawerProps {
  selectedStep: string | null
  workflowStepResults: WorkflowStepResult[]
}

function resultBadgeStyle(result?: string): { bg: string; color: string } {
  const r = (result ?? '').toUpperCase()
  if (r === 'MATCH' || r === 'CLEAR' || r === 'PASS') return { bg: 'rgba(52,211,153,.15)', color: '#34d399' }
  if (r === 'NO_MATCH' || r === 'FAIL') return { bg: 'rgba(248,113,113,.15)', color: '#f87171' }
  if (r === 'REFER' || r === 'REVIEW') return { bg: 'rgba(251,146,60,.15)', color: '#fb923c' }
  return { bg: 'rgba(148,163,184,.15)', color: '#94a3b8' }
}

export function StepDetailDrawer({ selectedStep, workflowStepResults }: StepDetailDrawerProps) {
  if (!selectedStep) return null

  const stepResult = workflowStepResults.find(
    s => (s.stepName ?? '').toUpperCase() === selectedStep.toUpperCase()
  )

  const processResults = stepResult?.processResults ?? []
  const resultStyle = resultBadgeStyle(stepResult?.result)

  return (
    <div
      className="rounded-[11px] overflow-hidden"
      style={{ border: '1px solid #233149', background: '#0e1828' }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3"
        style={{ borderBottom: '1px solid #1e2c44', background: '#0a1322' }}
      >
        <p className="text-[11px] font-semibold text-[#94a3b8]">
          Step detail · <span className="text-[#f1f5f9]">{StepLabel(selectedStep)}</span>
        </p>
        {stepResult?.result && (
          <span
            className="text-[9px] font-bold px-1.5 py-0.5 rounded"
            style={{ background: resultStyle.bg, color: resultStyle.color }}
          >
            {stepResult.result}
          </span>
        )}
        <span className="ml-auto text-[10px] text-[#475569]">
          {processResults.length} process result{processResults.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      {processResults.length === 0 ? (
        <p className="px-4 py-3 text-[11px] text-[#475569] italic">No process results for this step</p>
      ) : (
        <table className="w-full border-collapse">
          <thead>
            <tr style={{ background: '#0d1626' }}>
              <th className="px-4 py-2 text-left text-[10px] font-semibold text-[#475569] uppercase tracking-wide">Object Type</th>
              <th className="px-4 py-2 text-left text-[10px] font-semibold text-[#475569] uppercase tracking-wide">Result</th>
              <th className="px-4 py-2 text-left text-[10px] font-semibold text-[#475569] uppercase tracking-wide">Source</th>
            </tr>
          </thead>
          <tbody>
            {processResults.map((pr, i) => {
              const prStyle = resultBadgeStyle(pr.result)
              return (
                <tr
                  key={i}
                  style={{ borderTop: '1px solid #1a2740' }}
                >
                  <td className="px-4 py-2 text-[11px] text-[#94a3b8]">{pr.objectType ?? '—'}</td>
                  <td className="px-4 py-2">
                    {pr.result ? (
                      <span
                        className="text-[9px] font-bold px-1.5 py-0.5 rounded"
                        style={{ background: prStyle.bg, color: prStyle.color }}
                      >
                        {pr.result}
                      </span>
                    ) : (
                      <span className="text-[11px] text-[#475569]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-[11px] font-mono text-[#5b6b85]">
                    {pr.providerResult?.source ?? '—'}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ── Journey Visualization (container) ────────────────────────────────────────

interface JourneyVisualizationProps {
  view: 'pipeline' | 'flowchart'
  steps: WorkflowSteps
  workflowStepResults: WorkflowStepResult[]
  riskAssessment?: RiskAssessment | null
  isRunning: boolean
  selectedStep: string | null
  onStepClick: (step: string) => void
  entityId?: string | null
  workflowName?: string
  workflowExecutionId?: string | null
  overallStatus?: string
  webhookReceived?: boolean
}

export function JourneyVisualization({
  view,
  steps,
  workflowStepResults: _workflowStepResults,
  isRunning,
  selectedStep,
  onStepClick,
  entityId,
  workflowName,
  workflowExecutionId,
  overallStatus,
  webhookReceived,
}: JourneyVisualizationProps) {
  if (view === 'flowchart') {
    return (
      <WorkflowFlowchart
        steps={steps}
        isRunning={isRunning}
        selectedStep={selectedStep}
        onStepClick={onStepClick}
        entityId={entityId}
        workflowName={workflowName}
        workflowExecutionId={workflowExecutionId}
        overallStatus={overallStatus}
        webhookReceived={webhookReceived}
      />
    )
  }

  return (
    <WorkflowPipeline
      steps={steps}
      isRunning={isRunning}
      selectedStep={selectedStep}
      onStepClick={onStepClick}
    />
  )
}
